import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { EnhancedSeoHead } from "@appcaire/shared/seo";
import {
  generateArticleSchema,
  generateBreadcrumbSchema,
  combineSchemas,
} from "@appcaire/shared/seo";
import {
  Home,
  ArrowRight,
  CheckCircle,
  Phone,
  ShoppingCart,
  UtensilsCrossed,
  Calendar,
  SquareCheck,
} from "lucide-react";

/**
 * Static data hoisted outside component (React best practice: rendering-hoist-jsx)
 */
const PRIVATE_SERVICES = [
  {
    title: "Städning",
    description:
      "Hjälp med daglig städning, dammsugning, moppning och större städdagar.",
    icon: SquareCheck,
  },
  {
    title: "Tvätt",
    description: "Tvättning, torkning och vikning av kläder och textilier.",
    icon: ShoppingCart,
  },
  {
    title: "Matlagning",
    description: "Hjälp med matlagning, matplanering och inköp av matvaror.",
    icon: UtensilsCrossed,
  },
  {
    title: "Inköp",
    description: "Hjälp med dagligvaruinköp och andra inköp.",
    icon: ShoppingCart,
  },
  {
    title: "Administration",
    description: "Hjälp med post, räkningar och annan administration.",
    icon: Calendar,
  },
];

/**
 * HushallsnaraTjanster - Information about private household services
 *
 * This page explains private, commercial services available in Nacka
 * that are separate from municipal hemtjänst (home care services)
 */
const HushallsnaraTjanster: React.FC = () => {
  /**
   * Gets the correct absolute path for links (always root-level for standalone app)
   */
  const getPath = (path: string) => path;

  // Memoize schema generation (React best practice: rerender-memo)
  const articleSchema = useMemo(
    () =>
      generateArticleSchema({
        headline:
          "Hushållsnära Tjänster i Nacka – Privata tjänster utöver hemtjänst",
        description:
          "Läs mer om privata hushållsnära tjänster i Nacka. Tjänster du kan köpa utöver det som ingår i ditt biståndsbeslut. Städning, tvätt, matlagning och mer.",
        url: "https://nackahemtjanst.se/hushallsnara-tjanster",
        datePublished: "2025-12-22",
        dateModified: "2026-01-17",
      }),
    [],
  );

  const breadcrumbSchema = useMemo(
    () =>
      generateBreadcrumbSchema([
        { name: "Hem", url: "https://nackahemtjanst.se/" },
        {
          name: "Hushållsnära tjänster",
          url: "https://nackahemtjanst.se/hushallsnara-tjanster",
        },
      ]),
    [],
  );

  const combinedSchema = useMemo(
    () => combineSchemas([articleSchema, breadcrumbSchema]),
    [articleSchema, breadcrumbSchema],
  );

  return (
    <>
      <EnhancedSeoHead
        title="Hushållsnära Tjänster i Nacka – Privata tjänster utöver hemtjänst"
        description="Läs mer om privata hushållsnära tjänster i Nacka. Tjänster du kan köpa utöver det som ingår i ditt biståndsbeslut. Städning, tvätt, matlagning och mer."
        canonicalUrl="https://nackahemtjanst.se/hushallsnara-tjanster"
        ogType="article"
        keywords={[
          "hushållsnära tjänster Nacka",
          "privata hushållstjänster Nacka",
          "köpa städning Nacka",
          "hushållstjänster utan biståndsbeslut",
          "privata hushållstjänster utöver hemtjänst",
        ]}
        contentClassification="Consumer Portal"
        targetAudience="seniors, families"
        coverage="Sweden"
        publishDate="2025-12-22T00:00:00+01:00"
        modifiedDate="2026-01-17T00:00:00+01:00"
        author="Nacka Hemtjänst"
        schemaMarkup={combinedSchema ?? undefined}
        entities={[{ type: "Place", name: "Nacka" }]}
      />

      {/* Hero Section with Glass Effect */}
      <section className="relative bg-gradient-to-b from-blue-50 via-white to-white py-16 md:py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 via-transparent to-emerald-100/20" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 inline-block mb-6 shadow-glass-light">
            <Home className="w-16 h-16 text-blue-600" />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-surface-900 tracking-tight mb-6">
            Hushållsnära Tjänster
          </h1>
          <p className="text-xl md:text-2xl text-surface-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            Privata tjänster du kan köpa utöver det som ingår i ditt
            biståndsbeslut. För dig som behöver mer hjälp än vad hemtjänsten
            täcker.
          </p>
        </div>
      </section>

      {/* Services Introduction with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl shadow-glass-light p-8 md:p-12">
            <h2 className="text-3xl font-bold text-surface-900 mb-6">
              Vad är hushållsnära tjänster?
            </h2>
            <p className="text-lg text-surface-600 mb-6 leading-relaxed">
              <strong>
                Hushållsnära tjänster är privata, kommersiella tjänster
              </strong>{" "}
              som du kan köpa direkt från företag. Dessa tjänster är{" "}
              <strong>inte en del av hemtjänst</strong> (municipal omsorg) och
              ingår inte i ditt biståndsbeslut från Nacka kommun.
            </p>
            <p className="text-lg text-surface-600 mb-6 leading-relaxed">
              Om du behöver mer hjälp än vad som ingår i ditt biståndsbeslut,
              kan du köpa ytterligare hushållsnära tjänster privat. Detta kan
              vara användbart om du till exempel behöver extra städning, mer
              matlagning eller ytterligare hjälp med inköp än vad hemtjänsten
              täcker.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mt-8">
              <p className="text-lg text-amber-900 leading-relaxed">
                <strong>Viktigt att förstå:</strong> Hushållsnära tjänster är{" "}
                <strong>inte samma sak som hemtjänst</strong>. Hemtjänst är
                kommunal omsorg som bedöms och beslutas av Nacka kommun.
                Hushållsnära tjänster är privata tjänster som du köper direkt
                från företag.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-surface-50 to-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-surface-900 mb-4 text-center">
            Exempel på privata hushållsnära tjänster
          </h2>
          <p className="text-center text-surface-600 mb-12 max-w-3xl mx-auto">
            Dessa tjänster kan du köpa från privata företag i Nacka:
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRIVATE_SERVICES.map((service, index) => (
              <div
                key={index}
                className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light hover:shadow-glass-hover transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-blue-50">
                    <service.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-surface-900 mb-2">
                      {service.title}
                    </h3>
                    <p className="text-surface-600">{service.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl shadow-glass-light p-8 md:p-12">
            <h2 className="text-3xl font-bold text-surface-900 mb-6">
              Så fungerar privata hushållsnära tjänster
            </h2>
            <p className="text-lg text-surface-600 mb-8 leading-relaxed">
              Till skillnad från hemtjänst, som bedöms och beslutas av kommunen,
              köper du privata hushållsnära tjänster direkt från företag:
            </p>
            <div className="space-y-4 mb-8">
              {[
                "Du söker efter företag som erbjuder hushållsnära tjänster i Nacka",
                "Du kontaktar företaget direkt och diskuterar dina behov",
                "Du och företaget kommer överens om pris, omfattning och schema",
                "Du betalar företaget direkt för de tjänster du köper",
              ].map((step, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-lg text-surface-700">{step}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 pt-8 border-t border-glass-border bg-blue-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-surface-900 mb-4">
                Skillnaden mellan hemtjänst och privata tjänster
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="font-semibold text-surface-900 mb-2">
                    Hemtjänst (municipal omsorg):
                  </p>
                  <ul className="list-disc list-inside text-surface-700 space-y-1">
                    <li>Bedöms och beslutas av Nacka kommun</li>
                    <li>Ingår i ditt biståndsbeslut</li>
                    <li>Du betalar en avgift till kommunen</li>
                    <li>Reglerad av lag och kommunens riktlinjer</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-surface-900 mb-2">
                    Privata hushållsnära tjänster:
                  </p>
                  <ul className="list-disc list-inside text-surface-700 space-y-1">
                    <li>Köps direkt från privata företag</li>
                    <li>Ingår INTE i ditt biståndsbeslut</li>
                    <li>Du betalar marknadspris till företaget</li>
                    <li>Du bestämmer själv vad du behöver och vill köpa</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-br from-blue-700 to-blue-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-700/95 to-blue-600/95 backdrop-blur-sm" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="bg-glass-white/10 backdrop-blur-glass border border-white/20 rounded-2xl p-8 shadow-glass-light">
            <Phone className="w-12 h-12 mx-auto mb-6 opacity-80" />
            <h2 className="text-3xl font-bold mb-4">
              Behöver du hjälp att hitta privata hushållstjänster?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Om du behöver mer hjälp än vad hemtjänsten täcker, kan du söka
              efter privata företag som erbjuder hushållsnära tjänster. Många av
              de anordnare som levererar hemtjänst erbjuder också privata
              tjänster.
            </p>
            <a
              href="tel:0871880000"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-white text-blue-700 text-xl font-bold hover:bg-blue-50 transition-colors shadow-lg"
            >
              <Phone className="w-6 h-6" />
              Ring 08-718 80 00
            </a>
            <p className="mt-4 text-white/80 text-sm">Vardagar 08:00–17:00</p>
          </div>
        </div>
      </section>

      {/* Disclaimer with Glass Effect */}
      <section className="py-12 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light">
            <p className="text-sm text-surface-600 leading-relaxed">
              <strong>Viktig information:</strong> Denna webbplats är en
              oberoende guide och är inte en del av Nacka kommun. Privata
              hushållsnära tjänster är inte samma sak som hemtjänst. Hemtjänst
              bedöms och beslutas av{" "}
              <a
                href="https://www.nacka.se"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-600 font-medium hover:underline"
              >
                Nacka kommun
              </a>
              . Privata hushållsnära tjänster köper du direkt från företag.
            </p>
          </div>
        </div>
      </section>

      {/* Cross-link to hemtjanstguide.se */}
      <section className="py-12 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light">
            <p className="text-surface-600 mb-4">
              Letar du efter information om privata hushållstjänster i andra
              kommuner? På{" "}
              <a
                href="https://hemtjanstguide.se"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-600 font-medium hover:underline"
              >
                hemtjanstguide.se
              </a>{" "}
              hittar du information om hemtjänst och privata tjänster i alla
              Sveriges kommuner.
            </p>
          </div>
        </div>
      </section>

      {/* Related Links with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-surface-50 to-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-surface-900 mb-8 text-center">
            Ytterligare information
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              to={getPath("/ansoka")}
              className="flex items-start gap-4 p-6 rounded-xl bg-glass-white backdrop-blur-glass border border-glass-border hover:bg-glass-hover transition-all shadow-glass-light"
            >
              <ArrowRight className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-surface-900 hover:text-emerald-600 transition-colors">
                  Ansök om hemtjänst
                </h3>
                <p className="text-sm text-surface-600">
                  Steg-för-steg guide för att ansöka hos Nacka kommun
                </p>
              </div>
            </Link>
            <Link
              to={getPath("/hitta-hemtjanst")}
              className="flex items-start gap-4 p-6 rounded-xl bg-glass-white backdrop-blur-glass border border-glass-border hover:bg-glass-hover transition-all shadow-glass-light"
            >
              <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-surface-900 hover:text-blue-600 transition-colors">
                  Hitta hemtjänst
                </h3>
                <p className="text-sm text-surface-600">
                  Jämför anordnare och hitta den som passar dig bäst
                </p>
              </div>
            </Link>
            <Link
              to={getPath("/kontakt")}
              className="flex items-start gap-4 p-6 rounded-xl bg-glass-white backdrop-blur-glass border border-glass-border hover:bg-glass-hover transition-all shadow-glass-light"
            >
              <ArrowRight className="w-5 h-5 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-surface-900 hover:text-amber-600 transition-colors">
                  Kontakt
                </h3>
                <p className="text-sm text-surface-600">
                  Kontakta Nacka kommun direkt
                </p>
              </div>
            </Link>
            <Link
              to={getPath("/vanliga-fragor-och-svar")}
              className="flex items-start gap-4 p-6 rounded-xl bg-glass-white backdrop-blur-glass border border-glass-border hover:bg-glass-hover transition-all shadow-glass-light"
            >
              <ArrowRight className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-surface-900 hover:text-purple-600 transition-colors">
                  Vanliga frågor
                </h3>
                <p className="text-sm text-surface-600">
                  Svar på vanliga frågor om hemtjänst
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default HushallsnaraTjanster;
